"use client";

import { useEffect } from "react";
import { useLayerZeroFeeEstimation } from "@/hooks/useLayerZeroFeeEstimation";
import { InfoIcon } from "@/icons";

interface FeeDisplayProps {
  amount: string;
  tokenId: string;
  direction: "eth-to-camp" | "camp-to-eth";
}

export function FeeDisplay({ amount, tokenId, direction }: FeeDisplayProps) {
  const { isLoading, isError, error, nativeFee, lzTokenFee, totalFee, estimateFee } =
    useLayerZeroFeeEstimation();

  // Only show for Camp to Ethereum direction
  const shouldShow = direction === "camp-to-eth";

  useEffect(() => {
    if (shouldShow && amount && parseFloat(amount) > 0) {
      estimateFee(amount, tokenId);
    } else if (shouldShow && (!amount || parseFloat(amount) === 0)) {
      // Clear fee state when amount is 0 or empty
      estimateFee("", tokenId);
    }
  }, [shouldShow, amount, tokenId, estimateFee]);

  if (!shouldShow) {
    return null;
  }

  const formatFee = (fee: string | null) => {
    if (!fee) return "0.000";
    const num = parseFloat(fee);
    if (num < 0.001) return "< 0.001";
    return num.toFixed(6);
  };

  return (
    <div className="mt-3 rounded-md border border-orange-200 bg-orange-50 p-4">
      <div className="flex items-start gap-2">
        <InfoIcon size={16} color="#ea580c" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-orange-800">
            LayerZero Bridge Fees
          </h4>
          <p className="text-xs text-orange-700 mb-3">
            {amount && parseFloat(amount) > 0 ? (
              <>Estimated fees for bridging {amount} {tokenId.toUpperCase()} to Ethereum</>
            ) : (
              <>Enter an amount to see estimated fees</>
            )}
          </p>
          
          {isLoading && (
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-orange-600 border-t-transparent"></div>
              <span className="text-sm text-orange-700">Calculating fees...</span>
            </div>
          )}

          {isError && (
            <div className="text-sm text-red-600">
              Failed to estimate fees: {error}
            </div>
          )}

          {!isLoading && !isError && totalFee && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-orange-700">Native Fee:</span>
                  <span className="font-medium text-orange-800">{formatFee(nativeFee)} CAMP</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-orange-700">LZ Token Fee:</span>
                  <span className="font-medium text-orange-800">{formatFee(lzTokenFee)} CAMP</span>
                </div>
              </div>
              <div className="border-t border-orange-200 pt-2">
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-orange-800">Total Fee:</span>
                  <span className="text-orange-900">{formatFee(totalFee)} CAMP</span>
                </div>
              </div>
              <div className="text-xs text-orange-600 mt-2">
                Fees are paid in CAMP tokens and cover LayerZero&apos;s cross-chain messaging
              </div>
              <div className="text-xs text-orange-600 mt-1">
                Need CAMP tokens? Buy on{" "}
                <a
                  href="https://summitx.finance/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-orange-700"
                >
                  SummitX DEX
                </a>
                {" "}or check{" "}
                <a
                  href="https://coinmarketcap.com/currencies/camp-network/#Markets"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-orange-700"
                >
                  CEX markets
                </a>
              </div>
            </div>
          )}

          {!isLoading && !isError && !totalFee && amount && parseFloat(amount) > 0 && (
            <div className="text-sm text-yellow-700">
              Unable to estimate fees for this amount. Please try again.
            </div>
          )}

          {(!amount || parseFloat(amount) <= 0) && (
            <div className="text-sm text-orange-600">
              Enter an amount to see estimated fees.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
